/*
  Contact — image gallery: seamless infinite marquee (Pause Rituals)

  Transform-based (translateX) instead of native scroll, so auto-scroll, drag, wheel and
  release-inertia never fight each other:
    • auto-scroll runs while idle, PAUSES on hover / drag / hidden tab
    • drag (mouse + touch via Pointer Events) moves the strip 1:1, feels natural
    • on release it glides with inertia, then auto-scroll RESUMES from the current
      position — never resets to the start
    • true infinite loop: the rail repeats every "set", offset wraps modulo set-width, so
      the wrap point shows identical pixels → no seam, no jump, both directions
  Respects reduced-motion (drag still works). Re-inits inside the Theme Editor.
*/
(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class ContactGallery {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-cg-track]');
      if (!this.track) return;
      this.viewport = root.querySelector('.cg__viewport') || this.track.parentElement;

      this.autoplay = root.dataset.autoplay === 'true' && !reduceMotion;
      this.duration = Math.max(1, parseFloat(root.dataset.duration) || 30);
      this.durationMobile = Math.max(1, parseFloat(root.dataset.durationMobile) || this.duration);

      this.offset = 0;       // px; rendered as translateX(-offset). Kept in [0, setWidth)
      this.setWidth = 0;     // width of ONE original set (incl. trailing gap) = the loop period
      this.speed = 40;       // px/sec auto-scroll
      this.velocity = 0;     // px/frame inertia after release / arrow impulse

      this.hovered = false;
      this.hidden = false;
      this.dragging = false;
      this.wheelActive = false;

      this.rafId = null;
      this.lastTs = null;
      this.clones = [];
      this.retries = 0;
      this._wheelT = null;

      this.bindArrows();
      this.bindDrag();
      this.bindWheel();
      this.bindEnv();

      this.setup();
    }

    /* (re)build the seamless loop, retrying until the layout has real widths */
    setup() {
      this.removeClones();
      this.originals = Array.from(this.track.children).filter((n) => !n.classList.contains('cg__clone'));
      this.setWidth = this.measureSetWidth();
      if (this.setWidth < 5) {
        if (this.retries++ < 40) requestAnimationFrame(() => this.setup());
        return;
      }
      this.retries = 0;

      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      this.speed = this.setWidth / (isMobile ? this.durationMobile : this.duration);

      // duplicate the set until the rail is wide enough that, for ANY offset in
      // [0, setWidth), the viewport is always filled (>= 2 sets + one viewport width)
      const need = this.setWidth * 2 + this.viewport.clientWidth;
      let guard = 0;
      while (this.track.scrollWidth < need && guard++ < 24) {
        this.originals.forEach((node) => {
          const c = node.cloneNode(true);
          c.setAttribute('aria-hidden', 'true');
          c.classList.add('cg__clone');
          c.tabIndex = -1;
          this.track.appendChild(c);
          this.clones.push(c);
        });
      }

      this.wrap();
      this.render();
      this.start();
    }

    measureSetWidth() {
      const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
      let w = 0;
      this.originals.forEach((n) => { w += n.getBoundingClientRect().width + gap; });
      return w;
    }

    removeClones() {
      this.clones.forEach((c) => c.remove());
      this.clones = [];
    }

    wrap() {
      const w = this.setWidth;
      if (w > 0) this.offset = ((this.offset % w) + w) % w;
    }
    render() {
      this.track.style.transform = 'translate3d(' + -this.offset + 'px,0,0)';
    }

    idle() { return !this.hovered && !this.hidden && !this.dragging && !this.wheelActive; }

    start() {
      this.stop();
      const tick = (ts) => {
        if (this.lastTs == null) this.lastTs = ts;
        const dt = Math.min((ts - this.lastTs) / 1000, 0.05);
        this.lastTs = ts;

        if (this.dragging) {
          // pointer drives the offset directly — nothing to do here
        } else if (Math.abs(this.velocity) > 0.15) {
          // inertia glide after a drag release or an arrow nudge
          this.offset += this.velocity;
          this.velocity *= 0.93;
          if (Math.abs(this.velocity) <= 0.15) this.velocity = 0;
          this.wrap();
          this.render();
        } else if (this.autoplay && this.idle()) {
          // resume the marquee FROM THE CURRENT position
          this.offset += this.speed * dt;
          this.wrap();
          this.render();
        }
        this.rafId = requestAnimationFrame(tick);
      };
      this.rafId = requestAnimationFrame(tick);
    }
    stop() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = null;
      this.lastTs = null;
    }
    destroy() {
      this.stop();
      this.removeClones();
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    /* ---------- drag: Pointer Events unify mouse + touch ---------- */
    bindDrag() {
      let startX = 0, startOffset = 0, lastX = 0, moved = 0, pid = null;

      const down = (e) => {
        if (e.button != null && e.button !== 0) return;
        this.dragging = true;
        this.velocity = 0;
        moved = 0;
        startX = e.clientX;
        lastX = startX;
        startOffset = this.offset;
        try { this.track.setPointerCapture(e.pointerId); pid = e.pointerId; } catch (_) {}
      };
      const move = (e) => {
        if (!this.dragging) return;
        const delta = e.clientX - startX;
        moved = Math.abs(delta);
        if (moved > 5) this.track.classList.add('is-dragging');
        this.offset = startOffset - delta;   // drag right → reveal earlier content
        this.wrap();
        this.render();
        this.velocity = -(e.clientX - lastX); // glide continues the drag direction
        lastX = e.clientX;
      };
      const up = () => {
        if (!this.dragging) return;
        this.dragging = false;
        this.track.classList.remove('is-dragging');
        this.lastTs = null;
        if (pid != null) { try { this.track.releasePointerCapture(pid); } catch (_) {} pid = null; }
      };

      this.track.addEventListener('pointerdown', down);
      this.track.addEventListener('pointermove', move);
      this.track.addEventListener('pointerup', up);
      this.track.addEventListener('pointercancel', up);
      // if it was a drag, swallow the click so tiles don't navigate
      this.track.addEventListener('click', (e) => {
        if (moved > 5) { e.preventDefault(); e.stopPropagation(); }
      }, true);
    }

    /* ---------- wheel: horizontal / shift-wheel ---------- */
    bindWheel() {
      this.viewport.addEventListener('wheel', (e) => {
        const dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : (e.shiftKey ? e.deltaY : 0);
        if (!dx) return;          // vertical-only wheel → let the page scroll
        e.preventDefault();
        this.offset += dx;
        this.wrap();
        this.render();
        this.velocity = 0;
        this.wheelActive = true;
        clearTimeout(this._wheelT);
        this._wheelT = setTimeout(() => { this.wheelActive = false; this.lastTs = null; }, 500);
      }, { passive: false });
    }

    /* ---------- arrows: a smooth inertia impulse ---------- */
    bindArrows() {
      const prev = this.root.querySelector('[data-cg-prev]');
      const next = this.root.querySelector('[data-cg-next]');
      const step = () => {
        const first = this.track.querySelector('.cg__tile');
        const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
        return first ? first.getBoundingClientRect().width + gap : 200;
      };
      const nudge = (dir) => { this.velocity = dir * Math.max(9, step() / 13); this.lastTs = null; };
      if (prev) prev.addEventListener('click', () => nudge(-1));
      if (next) next.addEventListener('click', () => nudge(1));
    }

    /* ---------- env: hover / tab visibility / resize ---------- */
    bindEnv() {
      this.root.addEventListener('pointerenter', () => { this.hovered = true; });
      this.root.addEventListener('pointerleave', () => { this.hovered = false; this.lastTs = null; });
      document.addEventListener('visibilitychange', () => { this.hidden = document.hidden; this.lastTs = null; });
      let t = null;
      this._onResize = () => { clearTimeout(t); t = setTimeout(() => this.setup(), 200); };
      window.addEventListener('resize', this._onResize);
    }
  }

  function initOne(el) {
    if (el.__cg) el.__cg.destroy();
    el.__cg = new ContactGallery(el);
  }
  function initAll(scope) {
    const root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('[data-cg]').forEach(initOne);
    if (scope && scope.matches && scope.matches('[data-cg]')) initOne(scope);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:select', (e) => initAll(e.target));
  document.addEventListener('shopify:section:reorder', () => initAll());
})();
